import { Link } from 'react-router-dom'
import BigButton from '../components/BigButton'
import { nlDagMaand } from '../lib/format'
import { eventDateSentence, reopenSentence, useSeason } from '../lib/season'
import type { Season } from '../lib/types'

export default function Home() {
  const { season, loading: seasonLoading } = useSeason()
  // Buiten het seizoen (na de markt, tot mei) mag de pagina niemand meer naar
  // een gesloten formulier sturen. Zolang /api/season nog loopt is `season`
  // null maar de status gewoon nog onbekend — behandel dat dan niet alsof
  // het al open is, anders toont deze pagina even de marktdatum en
  // de inschrijfknop aan een bezoeker tijdens een effectief gesloten
  // seizoen, tot de fetch klaar is.
  const gesloten = season !== null && !season.registrationOpen
  // De marktdag van de lopende editie, uit /beheer. Leeg zolang hij nog niet
  // ingevuld is — elke zin hieronder heeft daarom een variant zonder datum,
  // want een oud jaartal laten staan is erger dan geen datum.
  const datum = eventDateSentence(season)
  const dagMaand = nlDagMaand(season?.eventDate)
  return (
    <>
      {/* Hero — full-bleed, lime block, pointing-hand from fluweelfeest.be */}
      <section className="bg-lime border-b-[3px] border-ink">
        <div className="mx-auto max-w-5xl px-5 pt-4 pb-16 sm:pt-5">
          <p className="font-display text-xs sm:text-sm tracking-display uppercase text-ink/80">
            {seasonLoading
              ? ''
              : gesloten
              ? `De editie${season?.editionYear ? ` van ${season.editionYear}` : ''} zit erop — bedankt!`
              : datum
              ? `${datum}.`
              : 'Inschrijven is open.'}
          </p>
          <div className="mt-10 grid sm:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <p className="font-display text-2xl tracking-display uppercase mb-2">Welkom op de</p>
            <h1 className="font-display text-7xl sm:text-9xl leading-[0.92] tracking-display uppercase">
              Rommel<br/>markt
            </h1>
            <p className="mt-3 font-display text-xl tracking-display uppercase text-ink/70">
              Onderdeel van het Fluweelfeest · €5 per plekje
            </p>
            <p className="mt-6 text-lg max-w-md">
              {seasonLoading
                ? ''
                : gesloten
                ? 'De buurtrommelmarkt van het Fluweelfeest is voor dit jaar afgelopen. Volgende editie kan je hier opnieuw een plekje reserveren.'
                : 'Schrijf je in voor een plekje op de buurtrommelmarkt van het Fluweelfeest. Buurtbewoners krijgen voorrang tot 31 augustus, daarna delen we de plekken uit.'}
            </p>
            {seasonLoading ? null : gesloten ? (
              <GeslotenBlok season={season} />
            ) : (
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/inschrijven">
                  <BigButton>Ik schrijf me in</BigButton>
                </Link>
              </div>
            )}
          </div>
          <img
            src="/img/pointing-hand.png"
            alt=""
            aria-hidden
            className="hidden sm:block w-64 lg:w-80 select-none -rotate-12"
          />
          </div>
        </div>
      </section>

      {/* Trust block — last year's market photo, red block like fluweelfeest.be */}
      <section className="bg-red text-offwhite border-b-[3px] border-ink">
        <div className="mx-auto max-w-5xl px-5 py-12 grid sm:grid-cols-2 gap-8 items-center">
          <img
            src="/img/fluweelmarkt.jpg"
            alt="Vorige editie van de rommelmarkt"
            className="w-full border-[3px] border-ink shadow-block -rotate-1"
            loading="lazy"
          />
          <div>
            <h2 className="font-display text-5xl tracking-display uppercase mb-3">Een dag in de straat</h2>
            <p className="text-lg">
              {/* season.eventDateLabel staat al in kleine letters ("zaterdag 12
                  september 2026") — midden in de zin is dat precies goed. */}
              {season?.eventDateLabel ? `Op ${season.eventDateLabel} zetten` : 'We zetten'}
              {' '}de Fluweelstraat vol kraampjes. Verkoop wat je niet
              meer gebruikt, kom een babbeltje doen, of slenter gewoon eens rond.
            </p>
          </div>
        </div>
      </section>

      {/* How it works — two parallel flows: buurtbewoner shortcut vs. open registration */}
      <section className="bg-lime border-b-[3px] border-ink">
        <div className="mx-auto max-w-5xl px-5 py-16">
          <h2 className="font-display text-5xl tracking-display uppercase mb-2">Hoe werkt het?</h2>
          <p className="text-lg mb-10 max-w-2xl">
            We doen het in twee fases. Welke voor jou geldt, hangt af van waar je woont.
          </p>

          <div className="mb-10">
            <h3 className="font-display text-3xl tracking-display uppercase mb-3">
              Woon je in de Fluweelstraat, Kempstraat of Tortelduifstraat?
            </h3>
            <div className="grid sm:grid-cols-4 border-[3px] border-ink bg-ink divide-y-[3px] sm:divide-y-0 sm:divide-x-[3px] divide-ink shadow-block">
              <Step n={1} title="Schrijf je in vóór 31 augustus" body="Naam, e-mailadres, het adres van je gevel. Eén minuutje werk." />
              <Step n={2} title="Wij houden je gevel vrij" body="Je krijgt meteen een bevestigingsmail met je plek én de betaalgegevens." />
              <Step n={3} title="Betaal €5 per plek" body="Via overschrijving of QR-scan. Je plek staat dan definitief vast." />
              <Step n={4} title={dagMaand ? `Tot ${dagMaand}` : 'Tot op de markt'} body="Op de dag zelf zet je je standje op voor je gevel." />
            </div>
          </div>

          <div>
            <h3 className="font-display text-3xl tracking-display uppercase mb-3">
              Woon je elders?
            </h3>
            <div className="grid sm:grid-cols-4 border-[3px] border-ink bg-ink divide-y-[3px] sm:divide-y-0 sm:divide-x-[3px] divide-ink shadow-block">
              <Step n={1} title="Schrijf je in" body="Vul je gegevens in en geef je gewenste straat door." />
              <Step n={2} title="Betaal €5 per plek" body="Via overschrijving of QR-scan. Je plek staat dan definitief vast." />
              <Step n={3} title="Op 1 september krijg je een mail" body="Daarin staat in welke zone we je een plek toewijzen." />
              <Step n={4} title="Kies zelf je plek" body="Tik op een groen vakje op de plattegrond. Klaar." />
            </div>
          </div>
        </div>
      </section>

      {/* Praktisch — de link die we naar iedereen sturen */}
      <section className="bg-offwhite border-b-[3px] border-ink">
        <div className="mx-auto max-w-5xl px-5 py-12 flex flex-col sm:flex-row gap-5 sm:items-center sm:justify-between">
          <div className="max-w-xl">
            <h2 className="font-display text-4xl sm:text-5xl tracking-display uppercase mb-2">
              Praktisch op de dag zelf
            </h2>
            <p className="text-lg">
              De uren, hoe groot je plek is, waar je je auto kwijt kan, wat je mag verkopen en hoe het zit
              met de betaling — alles op één pagina.
            </p>
          </div>
          <Link to="/praktisch" className="shrink-0">
            <BigButton>Praktische info</BigButton>
          </Link>
        </div>
      </section>

      {/* CTA strip — backed by the street-party photo with a black overlay */}
      <section
        className="relative bg-ink text-lime"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.78), rgba(0,0,0,0.78)), url('/img/street-party.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="mx-auto max-w-5xl px-5 py-12 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <p className="font-display text-3xl sm:text-4xl tracking-display uppercase">
            {seasonLoading ? ' ' : gesloten ? 'Volgende editie doe je mee?' : 'Klaar om mee te doen?'}
          </p>
          {seasonLoading ? null : gesloten ? (
            <p className="font-display text-2xl tracking-display uppercase text-lime">
              Inschrijven kan weer {reopenSentence(season)}
            </p>
          ) : (
            <Link to="/inschrijven">
              <BigButton variant="lime">Schrijf je nu in</BigButton>
            </Link>
          )}
        </div>
      </section>
    </>
  )
}

function GeslotenBlok({ season }: { season: Season | null }) {
  return (
    <div className="mt-8 border-[3px] border-ink bg-offwhite shadow-block p-5 max-w-md">
      <p className="font-display text-3xl tracking-display uppercase mb-1">
        Volgende editie
      </p>
      <p className="text-base">
        Inschrijven kan weer <strong>{reopenSentence(season)}</strong>.
      </p>
      <p className="text-base text-muted mt-2">
        Deed je mee? Met je persoonlijke link uit je mail kan je je inschrijving nog bekijken
        en ons feedback geven.
      </p>
    </div>
  )
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="bg-ink text-offwhite p-6">
      <div className="font-display text-2xl tracking-display uppercase text-lime mb-3">
        {n.toString().padStart(2, '0')}.&nbsp;{title}
      </div>
      <p className="text-offwhite/80">{body}</p>
    </div>
  )
}
